import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { colorOf, COLOR_OPTIONS, GRADE_GROUPS, GRADES } from "@/lib/subjects";
import { BookOpen, Plus, Trash2, Pencil, Save, X } from "lucide-react";

export default function AdminSubjects() {
  const [subjects, setSubjects] = useState([]);
  const [name, setName] = useState("");
  const [color, setColor] = useState("sky");
  const [grades, setGrades] = useState([...GRADES]);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/subjects").then(({ data }) => setSubjects(data));
  useEffect(() => { load(); }, []);

  const toggleGrade = (g) => setGrades((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g].sort());

  const resetForm = () => { setEditId(null); setName(""); setColor("sky"); setGrades([...GRADES]); };

  const startEdit = (s) => {
    setEditId(s.id);
    setName(s.name);
    setColor(s.color || "sky");
    setGrades(s.grades?.length ? [...s.grades] : [...GRADES]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Ders adı gerekli");
    try {
      if (editId) {
        await api.put(`/subjects/${editId}`, { name: name.trim(), color, grades });
        toast.success("Ders güncellendi ✓");
      } else {
        await api.post("/subjects", { name: name.trim(), color, grades });
        toast.success("Ders eklendi");
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu ders silinsin mi?")) return;
    try {
      await api.delete(`/subjects/${id}`);
      toast.success("Silindi");
      if (editId === id) resetForm();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-100 text-purple-600">
          <BookOpen className="h-6 w-6" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Ders Tanımları</h1>
          <p className="text-slate-500">Ders adı, rengi ve sınıflarını yönetin — ekleyin, düzenleyin, silin. Ders adını değiştirince bağlı tüm kayıtlar da güncellenir.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-sm font-semibold text-slate-700">Ders Adı</label>
            <input data-testid="subject-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="ör. Sosyal Bilgiler" className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Renk</label>
            <div className="flex gap-1.5">
              {COLOR_OPTIONS.map((c) => {
                const cc = colorOf(c);
                return (
                  <button key={c} data-testid={`color-${c}`} onClick={() => setColor(c)} className={`h-8 w-8 rounded-lg ${cc.solid} transition ${color === c ? "ring-2 ring-offset-2 ring-slate-400" : ""}`} />
                );
              })}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">Sınıflar</label>
            <div className="flex flex-wrap gap-3">
              {GRADE_GROUPS.map((grp) => (
                <div key={grp.label} className="flex items-center gap-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{grp.label}</span>
                  {grp.grades.map((g) => (
                    <button key={g} type="button" data-testid={`grade-${g}`} onClick={() => toggleGrade(g)} className={`h-9 w-9 rounded-lg border text-sm font-bold transition active:scale-95 ${grades.includes(g) ? "border-indigo-500 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-500"}`}>{g}</button>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <button data-testid="add-subject-btn" onClick={save} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 font-semibold text-white transition hover:bg-indigo-700 active:scale-95">
            {editId ? <><Save className="h-5 w-5" /> Güncelle</> : <><Plus className="h-5 w-5" /> Ekle</>}
          </button>
          {editId && (
            <button data-testid="cancel-edit-btn" onClick={resetForm} className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 font-semibold text-slate-500 transition hover:bg-slate-50 active:scale-95">
              <X className="h-5 w-5" /> İptal
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {subjects.map((s) => {
          const c = colorOf(s.color);
          return (
            <div key={s.id} data-testid={`subject-${s.id}`} className={`flex items-center justify-between rounded-2xl border ${c.border} ${c.bg} p-4`}>
              <div className={`flex items-center gap-2 font-bold ${c.text}`}>
                <span className={`h-3 w-3 rounded-full ${c.dot}`} /> {s.name}
                <span className="ml-1 rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{s.grades?.length ? s.grades.join("-") + ". sınıf" : "tüm sınıflar"}</span>
              </div>
              <div className="flex items-center gap-0.5">
                <button data-testid={`edit-subject-${s.id}`} onClick={() => startEdit(s)} className="rounded-lg p-1.5 text-slate-500 transition hover:bg-white/60 hover:text-indigo-600 active:scale-90">
                  <Pencil className="h-4 w-4" />
                </button>
                <button data-testid={`delete-subject-${s.id}`} onClick={() => remove(s.id)} className="rounded-lg p-1.5 text-slate-500 transition hover:bg-white/60 hover:text-rose-600 active:scale-90">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
